/**
 * @name currency
 * @description ISO 4217 currency support shared across the app.
 *
 * Money in this product is multi-currency at the document level: a purchase
 * order, a shipment and a payment can each be denominated differently, so
 * nothing here assumes USD. Callers pass the currency stored on the record and
 * this module handles symbol, placement and minor-unit rules.
 */

/**
 * Currencies with no minor unit. Formatting these with two decimals produces
 * amounts that look wrong to anyone actually trading in them (¥1,200.00).
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'ISK',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'UYI',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
]);

/** Gulf currencies quoted to three decimals. */
const THREE_DECIMAL_CURRENCIES = new Set([
  'BHD',
  'IQD',
  'JOD',
  'KWD',
  'LYD',
  'OMR',
  'TND',
]);

export const DEFAULT_CURRENCY = 'USD';

export type Currency = {
  code: string;
  name: string;
};

/**
 * Surfaced at the top of currency pickers. These cover the overwhelming
 * majority of import/export activity, so most users never scroll past them —
 * but every ISO 4217 code below remains selectable.
 */
export const COMMON_CURRENCY_CODES = [
  'USD',
  'EUR',
  'GBP',
  'CNY',
  'AUD',
  'SGD',
  'CAD',
  'JPY',
  'HKD',
  'INR',
  'NZD',
  'CHF',
  'KRW',
  'TWD',
  'THB',
  'VND',
  'MYR',
  'IDR',
  'PHP',
  'AED',
] as const;

/**
 * Active ISO 4217 codes. Names are the English short names; pickers localise
 * them at render time via `getCurrencyName`.
 */
export const ALL_CURRENCIES: readonly Currency[] = [
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'AFN', name: 'Afghan Afghani' },
  { code: 'ALL', name: 'Albanian Lek' },
  { code: 'AMD', name: 'Armenian Dram' },
  { code: 'ANG', name: 'Netherlands Antillean Guilder' },
  { code: 'AOA', name: 'Angolan Kwanza' },
  { code: 'ARS', name: 'Argentine Peso' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'AWG', name: 'Aruban Florin' },
  { code: 'AZN', name: 'Azerbaijani Manat' },
  { code: 'BAM', name: 'Bosnia-Herzegovina Convertible Mark' },
  { code: 'BBD', name: 'Barbadian Dollar' },
  { code: 'BDT', name: 'Bangladeshi Taka' },
  { code: 'BGN', name: 'Bulgarian Lev' },
  { code: 'BHD', name: 'Bahraini Dinar' },
  { code: 'BIF', name: 'Burundian Franc' },
  { code: 'BMD', name: 'Bermudan Dollar' },
  { code: 'BND', name: 'Brunei Dollar' },
  { code: 'BOB', name: 'Bolivian Boliviano' },
  { code: 'BRL', name: 'Brazilian Real' },
  { code: 'BSD', name: 'Bahamian Dollar' },
  { code: 'BTN', name: 'Bhutanese Ngultrum' },
  { code: 'BWP', name: 'Botswanan Pula' },
  { code: 'BYN', name: 'Belarusian Ruble' },
  { code: 'BZD', name: 'Belize Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'CDF', name: 'Congolese Franc' },
  { code: 'CHF', name: 'Swiss Franc' },
  { code: 'CLP', name: 'Chilean Peso' },
  { code: 'CNY', name: 'Chinese Yuan' },
  { code: 'COP', name: 'Colombian Peso' },
  { code: 'CRC', name: 'Costa Rican Colón' },
  { code: 'CUP', name: 'Cuban Peso' },
  { code: 'CVE', name: 'Cape Verdean Escudo' },
  { code: 'CZK', name: 'Czech Koruna' },
  { code: 'DJF', name: 'Djiboutian Franc' },
  { code: 'DKK', name: 'Danish Krone' },
  { code: 'DOP', name: 'Dominican Peso' },
  { code: 'DZD', name: 'Algerian Dinar' },
  { code: 'EGP', name: 'Egyptian Pound' },
  { code: 'ERN', name: 'Eritrean Nakfa' },
  { code: 'ETB', name: 'Ethiopian Birr' },
  { code: 'EUR', name: 'Euro' },
  { code: 'FJD', name: 'Fijian Dollar' },
  { code: 'FKP', name: 'Falkland Islands Pound' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'GEL', name: 'Georgian Lari' },
  { code: 'GHS', name: 'Ghanaian Cedi' },
  { code: 'GIP', name: 'Gibraltar Pound' },
  { code: 'GMD', name: 'Gambian Dalasi' },
  { code: 'GNF', name: 'Guinean Franc' },
  { code: 'GTQ', name: 'Guatemalan Quetzal' },
  { code: 'GYD', name: 'Guyanaese Dollar' },
  { code: 'HKD', name: 'Hong Kong Dollar' },
  { code: 'HNL', name: 'Honduran Lempira' },
  { code: 'HTG', name: 'Haitian Gourde' },
  { code: 'HUF', name: 'Hungarian Forint' },
  { code: 'IDR', name: 'Indonesian Rupiah' },
  { code: 'ILS', name: 'Israeli New Shekel' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'IQD', name: 'Iraqi Dinar' },
  { code: 'IRR', name: 'Iranian Rial' },
  { code: 'ISK', name: 'Icelandic Króna' },
  { code: 'JMD', name: 'Jamaican Dollar' },
  { code: 'JOD', name: 'Jordanian Dinar' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'KES', name: 'Kenyan Shilling' },
  { code: 'KGS', name: 'Kyrgystani Som' },
  { code: 'KHR', name: 'Cambodian Riel' },
  { code: 'KMF', name: 'Comorian Franc' },
  { code: 'KPW', name: 'North Korean Won' },
  { code: 'KRW', name: 'South Korean Won' },
  { code: 'KWD', name: 'Kuwaiti Dinar' },
  { code: 'KYD', name: 'Cayman Islands Dollar' },
  { code: 'KZT', name: 'Kazakhstani Tenge' },
  { code: 'LAK', name: 'Laotian Kip' },
  { code: 'LBP', name: 'Lebanese Pound' },
  { code: 'LKR', name: 'Sri Lankan Rupee' },
  { code: 'LRD', name: 'Liberian Dollar' },
  { code: 'LSL', name: 'Lesotho Loti' },
  { code: 'LYD', name: 'Libyan Dinar' },
  { code: 'MAD', name: 'Moroccan Dirham' },
  { code: 'MDL', name: 'Moldovan Leu' },
  { code: 'MGA', name: 'Malagasy Ariary' },
  { code: 'MKD', name: 'Macedonian Denar' },
  { code: 'MMK', name: 'Myanmar Kyat' },
  { code: 'MNT', name: 'Mongolian Tugrik' },
  { code: 'MOP', name: 'Macanese Pataca' },
  { code: 'MRU', name: 'Mauritanian Ouguiya' },
  { code: 'MUR', name: 'Mauritian Rupee' },
  { code: 'MVR', name: 'Maldivian Rufiyaa' },
  { code: 'MWK', name: 'Malawian Kwacha' },
  { code: 'MXN', name: 'Mexican Peso' },
  { code: 'MYR', name: 'Malaysian Ringgit' },
  { code: 'MZN', name: 'Mozambican Metical' },
  { code: 'NAD', name: 'Namibian Dollar' },
  { code: 'NGN', name: 'Nigerian Naira' },
  { code: 'NIO', name: 'Nicaraguan Córdoba' },
  { code: 'NOK', name: 'Norwegian Krone' },
  { code: 'NPR', name: 'Nepalese Rupee' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'OMR', name: 'Omani Rial' },
  { code: 'PAB', name: 'Panamanian Balboa' },
  { code: 'PEN', name: 'Peruvian Sol' },
  { code: 'PGK', name: 'Papua New Guinean Kina' },
  { code: 'PHP', name: 'Philippine Peso' },
  { code: 'PKR', name: 'Pakistani Rupee' },
  { code: 'PLN', name: 'Polish Złoty' },
  { code: 'PYG', name: 'Paraguayan Guarani' },
  { code: 'QAR', name: 'Qatari Rial' },
  { code: 'RON', name: 'Romanian Leu' },
  { code: 'RSD', name: 'Serbian Dinar' },
  { code: 'RUB', name: 'Russian Ruble' },
  { code: 'RWF', name: 'Rwandan Franc' },
  { code: 'SAR', name: 'Saudi Riyal' },
  { code: 'SBD', name: 'Solomon Islands Dollar' },
  { code: 'SCR', name: 'Seychellois Rupee' },
  { code: 'SDG', name: 'Sudanese Pound' },
  { code: 'SEK', name: 'Swedish Krona' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'SHP', name: 'St. Helena Pound' },
  { code: 'SLE', name: 'Sierra Leonean Leone' },
  { code: 'SOS', name: 'Somali Shilling' },
  { code: 'SRD', name: 'Surinamese Dollar' },
  { code: 'SSP', name: 'South Sudanese Pound' },
  { code: 'STN', name: 'São Tomé & Príncipe Dobra' },
  { code: 'SVC', name: 'Salvadoran Colón' },
  { code: 'SYP', name: 'Syrian Pound' },
  { code: 'SZL', name: 'Swazi Lilangeni' },
  { code: 'THB', name: 'Thai Baht' },
  { code: 'TJS', name: 'Tajikistani Somoni' },
  { code: 'TMT', name: 'Turkmenistani Manat' },
  { code: 'TND', name: 'Tunisian Dinar' },
  { code: 'TOP', name: 'Tongan Paʻanga' },
  { code: 'TRY', name: 'Turkish Lira' },
  { code: 'TTD', name: 'Trinidad & Tobago Dollar' },
  { code: 'TWD', name: 'New Taiwan Dollar' },
  { code: 'TZS', name: 'Tanzanian Shilling' },
  { code: 'UAH', name: 'Ukrainian Hryvnia' },
  { code: 'UGX', name: 'Ugandan Shilling' },
  { code: 'USD', name: 'US Dollar' },
  { code: 'UYU', name: 'Uruguayan Peso' },
  { code: 'UZS', name: 'Uzbekistani Som' },
  { code: 'VES', name: 'Venezuelan Bolívar' },
  { code: 'VND', name: 'Vietnamese Dong' },
  { code: 'VUV', name: 'Vanuatu Vatu' },
  { code: 'WST', name: 'Samoan Tala' },
  { code: 'XAF', name: 'Central African CFA Franc' },
  { code: 'XCD', name: 'East Caribbean Dollar' },
  { code: 'XOF', name: 'West African CFA Franc' },
  { code: 'XPF', name: 'CFP Franc' },
  { code: 'YER', name: 'Yemeni Rial' },
  { code: 'ZAR', name: 'South African Rand' },
  { code: 'ZMW', name: 'Zambian Kwacha' },
  { code: 'ZWG', name: 'Zimbabwe Gold' },
];

const CURRENCY_BY_CODE = new Map(
  ALL_CURRENCIES.map((currency) => [currency.code, currency]),
);

/**
 * @name isSupportedCurrency
 * @description Whether the value is an ISO 4217 code we know about.
 */
export function isSupportedCurrency(value: unknown): value is string {
  return typeof value === 'string' && CURRENCY_BY_CODE.has(value.toUpperCase());
}

/**
 * @name normalizeCurrencyCode
 * @description Narrow a free-form database value to a known currency code.
 *
 * Currency columns are plain text and rows predate validation, so anything
 * unrecognised falls back rather than throwing inside a render.
 */
export function normalizeCurrencyCode(
  value: string | null | undefined,
  fallback: string = DEFAULT_CURRENCY,
): string {
  if (typeof value !== 'string') {
    return fallback;
  }

  const code = value.trim().toUpperCase();

  return CURRENCY_BY_CODE.has(code) ? code : fallback;
}

/**
 * @name getCurrencyDecimals
 * @description Minor-unit digits for a currency: 0 for JPY/KRW/VND, 3 for the
 * Gulf dinars, 2 for everything else.
 */
export function getCurrencyDecimals(currency: string | null | undefined) {
  const code = normalizeCurrencyCode(currency);

  if (ZERO_DECIMAL_CURRENCIES.has(code)) {
    return 0;
  }

  return THREE_DECIMAL_CURRENCIES.has(code) ? 3 : 2;
}

/**
 * The most decimals any amount is ever shown with.
 *
 * Beyond a unit cost's four, because an allocated freight share or an
 * amortised sample cost is a division and lands wherever it lands.
 */
const MAX_DISPLAY_DECIMALS = 6;

/**
 * How many decimals an amount actually carries, ignoring trailing zeros.
 *
 * Read off the string rather than by repeated multiplication: 0.07 * 100 is
 * 7.000000000000001 in binary floating point, which counts as fifteen decimals
 * and would render "$0.0700".
 */
function significantDecimals(amount: number, cap: number): number {
  const [, fraction = ''] = Math.abs(amount).toFixed(cap).split('.');

  return fraction.replace(/0+$/, '').length;
}

/**
 * @name getDisplayDecimals
 * @description How many decimals to render an amount with.
 *
 * The same reasoning as `getUnitCostDecimals`, applied to every amount rather
 * than only to unit costs, because the two disagreeing is what put "$0" in one
 * cell of a table and "$0.00" in the totals row beneath it.
 *
 *   - a whole amount takes none. "$0.00" and "$273.00" spend three characters
 *     saying there is nothing after the point, and a column of them is a
 *     column of noise.
 *   - an amount finer than the currency's minor unit keeps the digits that
 *     carry its value. A label supplier quoting $0.0091 was being shown
 *     $0.01, which is a different price.
 *   - everything between takes the currency's own minor unit, so $1.50 does
 *     not collapse to $1.5.
 */
export function getDisplayDecimals(
  value: number,
  currency: string | null | undefined,
): number {
  if (!Number.isFinite(value) || Number.isInteger(value)) return 0;

  return Math.min(
    Math.max(
      significantDecimals(value, MAX_DISPLAY_DECIMALS),
      getCurrencyDecimals(currency),
    ),
    MAX_DISPLAY_DECIMALS,
  );
}

/**
 * How many decimals a table of money should pad to.
 *
 * A column that mixes "$4,940" with "$5,867.50" is hard to scan: the cents
 * look like a different kind of number. If any amount in the table has cents
 * (two or more decimal places), every amount is padded to the currency's
 * minor unit so they line up. If every amount is a whole unit, decimals stay
 * off. Zero-decimal currencies (JPY) always return 0.
 *
 * Finer-than-cent figures still keep their extra digits at format time
 * (`resolveTableMoneyDecimals`); this only decides the table-wide floor.
 */
export function getTableMoneyDecimals(
  values: ReadonlyArray<number | string | null | undefined>,
  currency?: string | null,
): number {
  const minor = getCurrencyDecimals(currency);

  if (minor === 0) return 0;

  for (const value of values) {
    const amount = Number(value);

    if (!Number.isFinite(amount)) continue;

    if (getDisplayDecimals(amount, currency) >= 2) {
      return minor;
    }
  }

  return 0;
}

/**
 * Decimals for one cell inside a table that has already chosen a floor.
 *
 * `undefined` means "use the amount's own rule", which is what a table of
 * whole dollars wants. When the table has cents, whole amounts pad to that
 * floor and amounts finer than a cent keep the digits they need.
 */
export function resolveTableMoneyDecimals(
  value: number | string | null | undefined,
  tableDecimals: number,
  currency?: string | null,
): number | undefined {
  if (tableDecimals <= 0) return undefined;

  const amount = Number(value);
  const own = Number.isFinite(amount)
    ? getDisplayDecimals(amount, currency)
    : 0;

  return Math.max(tableDecimals, own);
}

/**
 * Format one amount using the table-wide cents rule.
 */
export function formatTableMoney(
  params: FormatMoneyParams & {
    tableValues: ReadonlyArray<number | string | null | undefined>;
  },
): string {
  const { tableValues, decimals, ...rest } = params;
  const tableDecimals =
    decimals ?? getTableMoneyDecimals(tableValues, rest.currency);

  return formatMoney({
    ...rest,
    decimals: resolveTableMoneyDecimals(
      rest.value,
      tableDecimals,
      rest.currency,
    ),
  });
}

/**
 * @name getCurrencyName
 * @description Localised display name, falling back to the English short name.
 */
export function getCurrencyName(
  currency: string | null | undefined,
  locale = 'en-US',
) {
  const code = normalizeCurrencyCode(currency);

  try {
    const displayNames = new Intl.DisplayNames([locale], { type: 'currency' });

    return displayNames.of(code) ?? CURRENCY_BY_CODE.get(code)?.name ?? code;
  } catch {
    return CURRENCY_BY_CODE.get(code)?.name ?? code;
  }
}

/**
 * @name getCurrencySymbol
 * @description The narrow symbol for a currency ($, €, ¥), or the code itself
 * when the runtime has no symbol for it.
 */
export function getCurrencySymbol(
  currency: string | null | undefined,
  locale = 'en-US',
) {
  const code = normalizeCurrencyCode(currency);

  try {
    const parts = new Intl.NumberFormat(locale, {
      currency: code,
      currencyDisplay: 'narrowSymbol',
      style: 'currency',
    }).formatToParts(0);

    return parts.find((part) => part.type === 'currency')?.value ?? code;
  } catch {
    return code;
  }
}

/**
 * A "symbol" here means a real symbol glyph — $, €, £, ¥, ₹. Intl also hands
 * back letter abbreviations for currencies that have no glyph (SEK → "kr",
 * CHF → "CHF", MYR → "RM"), and those read as noise glued to the number
 * ("kr 1,000"). Any letter in the symbol therefore disqualifies it, and the
 * amount takes a trailing ISO code instead: "1,000.00 SEK".
 */
export function getCurrencyGlyph(
  currency: string | null | undefined,
  locale = 'en-US',
) {
  const symbol = getCurrencySymbol(currency, locale);

  return /\p{L}/u.test(symbol) ? null : symbol;
}

type FormatMoneyParams = {
  value: number | string | null | undefined;
  /** ISO 4217 code stored on the record. Falls back to USD when absent. */
  currency?: string | null;
  locale?: string;
  /**
   * Defaults to `auto`: the currency's symbol when it has one ($1,000.00), and
   * the amount with a trailing ISO code when it does not (1,000.00 SEK).
   * Pass `code` to force the ISO form where the dollar in "$1,000" would be
   * genuinely ambiguous between, say, a USD and an AUD quote.
   */
  currencyDisplay?: 'auto' | 'symbol' | 'code' | 'narrowSymbol';
  /** Override the currency's natural minor-unit digits. */
  decimals?: number;
};

/**
 * @name formatMoney
 * @description Format an amount in its own currency.
 *
 * Replaces the hardcoded `$`-prefixed helpers this app used to carry: those
 * rendered an AUD or SGD order as a US dollar amount, and forced two decimals
 * onto currencies that have none.
 */
export function formatMoney({
  currency,
  currencyDisplay = 'auto',
  decimals,
  locale = 'en-US',
  value,
}: FormatMoneyParams): string {
  const code = normalizeCurrencyCode(currency);
  const amount = Number(value);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const fractionDigits = decimals ?? getDisplayDecimals(safeAmount, code);

  if (currencyDisplay === 'auto') {
    const glyph = getCurrencyGlyph(code, locale);

    if (!glyph) {
      return `${formatAmount({ currency: code, decimals: fractionDigits, locale, value: safeAmount })} ${code}`;
    }
  }

  const options: Intl.NumberFormatOptions = {
    currency: code,
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    style: 'currency',
  };

  try {
    return new Intl.NumberFormat(locale, {
      ...options,
      currencyDisplay:
        currencyDisplay === 'auto' ? 'narrowSymbol' : currencyDisplay,
    }).format(safeAmount);
  } catch {
    // `narrowSymbol` is unsupported on some older runtimes; retry plainly.
    return new Intl.NumberFormat(locale, options).format(safeAmount);
  }
}

/**
 * @name MAX_UNIT_COST_DECIMALS
 * @description The most decimal places a unit cost can be entered with.
 *
 * Four, because label, film and packaging suppliers quote to four and nobody
 * quotes to five.
 *
 * Storage is no longer the tighter limit. `order_line_items.unit_price` was
 * numeric(10, 2) — which rounded four-decimal quotes on the way in, and defeated
 * two attempts to widen it because four views read the column — until
 * 20260908140000 took it to numeric(20, 6). `product_variants.unit_cost` is
 * bare `numeric` and always could hold four.
 *
 * Formatting never depended on any of that: it renders the decimals a value
 * actually has, so a row still carrying an old rounded price shows two and a
 * row written since shows four.
 */
export const MAX_UNIT_COST_DECIMALS = 4;

/**
 * @name getUnitCostDecimals
 * @description How many decimals to render a unit cost with.
 *
 * A unit cost is not money in the ordinary sense. Most are round — $3, $12 —
 * and forcing `.00` onto those adds noise to a column people scan. The ones
 * that are not round are usually cents. And a few are quoted at four decimals,
 * where rounding to cents misstates a 150,000-unit order by double digits.
 *
 * So the number decides: none when it is whole, two when cents are enough, and
 * up to four when the figure genuinely carries them. Nothing is padded beyond
 * what was entered, and nothing is truncated below it.
 */
export function getUnitCostDecimals(
  value: number | string | null | undefined,
): number {
  const amount = Number(value);

  if (!Number.isFinite(amount) || Number.isInteger(amount)) {
    return 0;
  }

  return Math.min(
    Math.max(significantDecimals(amount, MAX_UNIT_COST_DECIMALS), 2),
    MAX_UNIT_COST_DECIMALS,
  );
}

/**
 * @name isWithinUnitCostPrecision
 * @description Whether a unit cost carries no more decimals than we accept.
 *
 * Four is the input ceiling, so a pasted 0.123456 is rejected at the form
 * rather than silently rounded on the way into the column.
 */
export function isWithinUnitCostPrecision(
  value: number | string | null | undefined,
): boolean {
  if (value == null || value === '') {
    return true;
  }

  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return true;
  }

  return (
    Math.abs(amount * 10 ** MAX_UNIT_COST_DECIMALS) % 1 < Number.EPSILON * 1e6
  );
}

/**
 * @name formatUnitCost
 * @description Format a per-unit figure under the rule above.
 *
 * For any other amount — an order total, a freight bill, a balance — use
 * `formatMoney`, which keeps the currency's own minor units so a column of
 * totals stays aligned.
 */
export function formatUnitCost(
  params: Omit<FormatMoneyParams, 'decimals'>,
): string {
  return formatMoney({
    ...params,
    decimals: getUnitCostDecimals(params.value),
  });
}

/**
 * Truncate toward zero to `decimals` places, for display only.
 *
 * 0.6096 at two decimals is 0.60, not 0.61. `Intl` and `toFixed` both
 * round half-up, which is the opposite of this rule.
 */
export function floorMoneyTowardZero(value: number, decimals: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  if (!Number.isInteger(decimals) || decimals < 0) {
    return value;
  }

  const sign = value < 0 ? -1 : 1;
  const [whole, fraction = ''] = Math.abs(value)
    .toFixed(decimals + 8)
    .split('.');
  const kept = fraction.slice(0, decimals);

  return sign * Number(kept.length > 0 ? `${whole}.${kept}` : whole);
}

/**
 * How many decimals to show an allocated per-unit share with.
 *
 * Freight/unit and landed/unit are divisions, so they pick up extra
 * decimals the unit cost never had. Showing those next to a $5.20
 * unit cost made a $0.6096 freight share look more precise than the
 * price it was allocated from. Four decimals stay only when the unit
 * cost itself is quoted to four.
 *
 * Pass every contributing unit cost (latest, average, each invoice
 * line) when the figure is an average across batches: four decimals
 * only if one of those sources actually has four.
 */
export function getAllocatedUnitDecimals(
  unitCost:
    | number
    | string
    | null
    | undefined
    | ReadonlyArray<number | string | null | undefined>,
): 2 | 4 {
  if (Array.isArray(unitCost)) {
    return unitCost.some((cost) => getAllocatedUnitDecimals(cost) === 4)
      ? 4
      : 2;
  }

  const single = unitCost as number | string | null | undefined;

  // Anything below four (including a three-decimal quote) floors to two.
  return getUnitCostDecimals(single) >= MAX_UNIT_COST_DECIMALS ? 4 : 2;
}

type FormatAllocatedUnitMoneyParams = Omit<FormatMoneyParams, 'decimals'> & {
  /**
   * The product unit cost that decides 2 vs 4 decimal display. An
   * array means "four only if any of these sources is quoted to four".
   */
  unitCost:
    | number
    | string
    | null
    | undefined
    | ReadonlyArray<number | string | null | undefined>;
};

/**
 * @name formatAllocatedUnitMoney
 * @description Format freight/unit or landed/unit keyed off unit-cost precision.
 *
 * The allocation math is left alone. This only decides how the share is
 * printed: floor toward zero to two decimals unless the unit cost itself
 * carries four.
 */
export function formatAllocatedUnitMoney({
  unitCost,
  ...params
}: FormatAllocatedUnitMoneyParams): string {
  const decimals = getAllocatedUnitDecimals(unitCost);
  const amount = Number(params.value);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const floored = floorMoneyTowardZero(safeAmount, decimals);

  return formatMoney({
    ...params,
    // A whole share (zero freight, a round dollar) keeps the table's
    // usual "$0" / "$3", not a padded "$0.00" next to "$5.80".
    decimals: Number.isInteger(floored) ? undefined : decimals,
    value: floored,
  });
}

/**
 * @name formatAmount
 * @description Format the number alone, with the currency named elsewhere.
 *
 * Used by generated PDFs, whose built-in fonts cover a single-byte encoding
 * only and would render non-Latin currency symbols as the wrong glyph.
 */
export function formatAmount({
  currency,
  decimals,
  locale = 'en-US',
  value,
}: Omit<FormatMoneyParams, 'currencyDisplay'>): string {
  const amount = Number(value);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  const fractionDigits = decimals ?? getDisplayDecimals(safeAmount, currency);

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  }).format(safeAmount);
}

/**
 * @name getSharedCurrency
 * @description The one currency a set of records has in common, or `null` when
 * they are mixed.
 *
 * Adding amounts denominated in different currencies produces a meaningless
 * number, so any total across records has to check this first.
 */
export function getSharedCurrency(
  currencies: Array<string | null | undefined>,
): string | null {
  const codes = new Set<string>();

  for (const currency of currencies) {
    // A record with no stated currency cannot confirm a shared one. Running
    // it through the USD fallback here would let a set of currency-less
    // records claim a confident `$` the data does not support.
    const code = normalizeCurrencyCode(currency, '');

    if (!code) {
      return null;
    }

    codes.add(code);
  }

  if (codes.size !== 1) {
    return null;
  }

  return codes.values().next().value ?? null;
}

export type CurrencyTotal = {
  currency: string;
  total: number;
};

/**
 * @name sumByCurrency
 * @description Total a set of amounts, grouped by their own currency and
 * ordered largest first.
 *
 * Until rates are recorded against each document, a mixed-currency set has no
 * single correct total — so callers render one figure per currency rather than
 * a single converted one that would be quietly wrong.
 */
export function sumByCurrency(
  items: Array<{
    amount: number | string | null | undefined;
    currency?: string | null;
  }>,
): CurrencyTotal[] {
  const totals = new Map<string, number>();

  for (const item of items) {
    const code = normalizeCurrencyCode(item.currency);
    const amount = Number(item.amount);

    totals.set(
      code,
      (totals.get(code) ?? 0) + (Number.isFinite(amount) ? amount : 0),
    );
  }

  return [...totals.entries()]
    .map(([currency, total]) => ({ currency, total }))
    .sort((a, b) => b.total - a.total);
}

/**
 * @name getCurrencyOptions
 * @description Options for a currency picker: the common trade currencies
 * first, then every remaining ISO 4217 code alphabetically.
 */
export function getCurrencyOptions(locale = 'en-US') {
  const common = COMMON_CURRENCY_CODES.map((code) => ({
    code,
    group: 'common' as const,
    label: `${code} (${getCurrencyName(code, locale)})`,
  }));

  const rest = ALL_CURRENCIES.filter(
    (currency) =>
      !COMMON_CURRENCY_CODES.includes(
        currency.code as (typeof COMMON_CURRENCY_CODES)[number],
      ),
  ).map((currency) => ({
    code: currency.code,
    group: 'all' as const,
    label: `${currency.code} (${getCurrencyName(currency.code, locale)})`,
  }));

  return [...common, ...rest];
}
